import type { LinkExternal } from '@backmarket/http-api/src/api-models/LinkExternal'
import type { Bcp47Locale } from '@backmarket/http-api/src/standards/Locale'
import type { I18n } from '@backmarket/nuxt-module-i18n/types/i18n'
import { hashObjects } from '@backmarket/utils/object/hashObjects'

import { getFilenameFromUrl } from '../../../../utils/helpers'
import type {
  BuybackOrderConversation,
  BuybackOrderConversationMetadata,
  BuybackOrderMessage,
} from '../../api/conversation/types'
import {
  ROLES,
  type Role,
} from '../../components/OrderConversation/OrderConversation.constants'

export const detailsAdapter = (
  details: BuybackOrderConversationMetadata,
  role: Role,
  id: string,
  i18n: I18n,
  locale: Bcp47Locale,
) => {
  let recipient = details.id || ''
  let backLink = ''

  if (role === ROLES.MERCHANT) {
    recipient = `${details?.customer?.firstName} ${details?.customer?.lastName}`
    backLink = `/bo-seller/buyback/orders/${id}`
  }

  if (role === ROLES.CUSTOMER) {
    recipient = details?.refurbisher?.companyName ?? ''
    backLink = `/${locale}/dashboard/sales`
  }

  if (role === ROLES.STAFF) {
    recipient = details.id
    backLink = `/bo-staff/customer-care-manager/search?buybackId=${id}`
  }

  return {
    image: details.product.image.light,
    title: details.product.title,
    price: i18n.price(details.price),
    counterOfferPrice: details.counterOfferPrice
      ? i18n.price(details.counterOfferPrice)
      : '',
    status: details.state,
    recipient,
    backLink,
    showMessageBar: details.isAllowedToPostMessage,
  }
}

const messageAdapter = (message: BuybackOrderMessage) => {
  const { content, date: isoDate, attachments } = message
  const id = hashObjects({ content, isoDate })

  const formattedAttachments =
    attachments.map((file: LinkExternal) => {
      const { name, extension } = getFilenameFromUrl(file.href)

      return {
        ...file,
        name,
        extension,
      }
    }) || []

  return {
    id,
    content,
    isoDate,
    sender: message.author?.name || null,
    role: message.author.role,
    attachments: formattedAttachments,
  }
}

export const conversationAdapter = (
  conversation: BuybackOrderConversation,
  storedMessages: BuybackOrderMessage[],
) => {
  const { count } = conversation
  if (count === 0) {
    return {
      count: 0,
      next: null,
      previous: null,
      messages: [],
    }
  }

  const getPage = (url: string) => {
    if (url === null) return null

    return new URL(url).searchParams.get('page')
  }

  const next = conversation.next ? getPage(conversation.next) : null
  const previous = conversation.previous ? getPage(conversation.previous) : null

  const messages = storedMessages
    .map((message) => messageAdapter(message))
    .sort(
      (a, b) => new Date(a.isoDate).getTime() - new Date(b.isoDate).getTime(),
    )

  return {
    count,
    next,
    previous,
    messages,
  }
}
